<template>
	<div class="saleDaysSummary">
		<div v-if="isLoading" class="ml-03 mt-05 flex items-center gap-05">
			<span class="font-body-m text-ink-60">{{ t('saleDaysSummaryPanel.loading') }}</span>

			<Loader size="big" />
		</div>

		<div v-else-if="data && data.stores" class="grid grid-cols-[1fr_auto_auto_auto_auto_auto]">
			<template v-for="store in data.stores" :key="store.storeName">
				<div
					:class="[
						'portalColor font-body-s-medium col-span-6 flex w-full items-center justify-between rounded-t-sm py-01 pl-05 pr-03 text-ink-inverted-87',
						`portal--${store.portal}`
					]"
				>
					<div class="flex items-center">
						<PortalIcon :portal="store.portal" class="icon--size-1 mr-03" />

						{{ store.name }} ({{ numberOfSKUsPerStore.get(store.storeName) }})
					</div>

					<div class="flex items-center">
						<div class="score">{{ t('saleDaysSummaryPanel.score') }} {{ formatScore(store.score) }}%</div>

						<IconButton
							icon="chevron-down"
							variant="invert"
							size="xs"
							:class="['expandIcon ml-05 cursor-pointer', { expandIcon__unrotated: store.collapsed }]"
							@click="store.collapsed = !store.collapsed"
						/>
					</div>
				</div>

				<template v-if="!store.collapsed">
					<div :class="`portalColor-20 portal--${store.portal} rounded-bl-sm`">&nbsp;</div>
					<div :class="`portalColor-20 portal--${store.portal} columnHeaderCell`">
						{{ t('saleDaysSummaryPanel.available') }}
					</div>
					<div :class="`portalColor-20 portal--${store.portal} columnHeaderCell`">
						{{ t('saleDaysSummaryPanel.onSale') }}
					</div>
					<div :class="`portalColor-20 portal--${store.portal} columnHeaderCell`">
						{{ t('saleDaysSummaryPanel.missed') }}
					</div>
					<div :class="`portalColor-20 portal--${store.portal} columnHeaderCell`">
						{{ t('saleDaysSummaryPanel.score') }}
					</div>
					<div :class="`portalColor-20 portal--${store.portal} rounded-br-sm`">&nbsp;</div>

					<template v-for="product in store.products" :key="product.name">
						<div class="col-span-6 mt-02 flex w-full items-center justify-between overflow-hidden px-0 py-02 text-ink-38">
							<div class="flex items-center overflow-hidden">
								<span class="font-paragraph-s overflow-x-hidden text-ellipsis whitespace-nowrap">
									<TooltipIfEllipsis :tooltip-text="product.name">
										{{ product.name }}
									</TooltipIfEllipsis>
								</span>

								<div :class="['expandIcon mr-02 cursor-pointer hover:text-ink-60', { expandIcon__rotated: product.collapsed }]">
									<Icon name="chevron-down" size="1.5" @click="product.collapsed = !product.collapsed" />
								</div>
							</div>
						</div>

						<template v-if="!product.collapsed">
							<template v-for="sku in product.skus" :key="sku.uniqueSkuId">
								<div
									:class="`font-body-s mb-01 overflow-x-hidden rounded-l-sm border-l-4 bg-level-01 pl-04 pr-03 text-ink-87 border-left-score-${getScoreColor(sku.score)} flex items-center justify-between`"
								>
									<span class="overflow-x-hidden text-ellipsis whitespace-nowrap py-02">
										<TooltipIfEllipsis :tooltip-text="sku.humanName">
											{{ sku.humanName }}
										</TooltipIfEllipsis>
									</span>
								</div>
								<div class="valueCell text-ink-87">{{ sku.availableSaleDays }}</div>
								<div class="valueCell text-ink-87">{{ sku.actualSaleDays }}</div>
								<div :class="`valueCell text-score-${getScoreColor(sku.score)}`">
									{{ sku.missedSaleDays }}
								</div>
								<div :class="`valueCell text-score-${getScoreColor(sku.score)}`">{{ formatScore(sku.score, 0) }}%</div>

								<div class="mb-01 rounded-r-sm bg-level-01 px-03">
									<Tooltip :content="t('saleDaysSummaryPanel.viewTimelinesTooltip')">
										<IconButton
											class="showHistoryButton"
											size="xs"
											icon="calendar-text-outline"
											@click="selectSKU(sku, store, product.name)"
										/>
									</Tooltip>
								</div>
							</template>
						</template>
					</template>
				</template>

				<div class="col-span-6 mb-05">
					<!-- Spacer under every store -->
				</div>
			</template>
		</div>

		<div v-else class="text-ink-87">{{ t('saleDaysSummaryPanel.noData') }}</div>
	</div>

	<ScoreDetailsForSKUModal v-if="selectedSKUData" :data="selectedSKUData" @close="selectedSKUData = undefined" />
</template>

<script setup lang="ts">
import { Icon, IconButton, Loader, PortalIcon, Tooltip, TooltipIfEllipsis, useTranslation } from '@bluebrick/saas-ui'
import { cloneDeep, pick } from 'lodash-es'
import { computed, ref, toRefs, watch } from 'vue'
import { formatScore, getScoreColor } from '@/common/utils'
import { type SaleDaysSummaryPeriod } from '@/features/preferences/types'
import ScoreDetailsForSKUModal from '@/features/saleDaysSummary/components/ScoreDetailsForSKUModal.vue'
import {
	type SaleDaysSummaryDataForSKU,
	type SaleDaysDetailsForSKU,
	type SaleDaysSummaryDataForUI,
	type SaleDaysSummaryDataForStore
} from '@/features/saleDaysSummary/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const { t } = useTranslation()
const { trackEvent } = useTelemetry()

const props = defineProps<{
	data: SaleDaysSummaryDataForUI
	period: SaleDaysSummaryPeriod
	isLoading?: boolean
}>()

const { data } = toRefs(props)

const localData = ref(props.data)

const selectedSKUData = ref<SaleDaysDetailsForSKU>()

const numberOfSKUsPerStore = computed(() =>
	data.value.stores.reduce((result, store) => {
		result.set(
			store.storeName,
			store.products.reduce((sum, product) => sum + product.skus.length, 0)
		)

		return result
	}, new Map<string, number>())
)

function selectSKU(sku: SaleDaysSummaryDataForSKU, store: SaleDaysSummaryDataForStore, productName: string) {
	trackEvent('user', 'Opened recommended timeline.', {
		storeName: store.storeName,
		data: JSON.stringify(pick(sku, ['actualSaleDays', 'availableSaleDays', 'missedSaleDays', 'score']))
	})

	selectedSKUData.value = {
		...sku,
		portal: store.portal,
		storeName: store.name,
		storeId: store.storeName,
		productName,
		recommendedTimeline: sku.recommendedTimeline ?? store.recommendedTimeline ?? [],
		startDate: props.data.startDate,
		endDate: props.data.endDate,
		period: props.period
	}
}

watch(
	props.data,
	() => {
		localData.value = cloneDeep(props.data)
	},
	{ immediate: true }
)
</script>

<style lang="scss">
/*
Importing `saas-ui.css` is necessary for @apply to work with font names.
But it also messes up default style specificity a bit (margins and paddings become `0` even
if using `p-*` or `m-*` classes).
Therefore we leave this stylesheet as NOT scoped which solves the problem
 */
@import '@bluebrick/saas-ui/saas-ui.css';

.saleDaysSummary {
	.expandIcon {
		transition: all 0.1s linear;

		&__rotated {
			transform: rotate(-90deg);
		}

		&__unrotated {
			transform: rotate(90deg);
		}
	}

	.columnHeaderCell {
		@apply font-caption whitespace-nowrap px-03 py-02 text-right text-ink-60;
	}

	.valueCell {
		@apply font-body-s-medium mb-01 bg-level-01 px-03 py-02 text-right;
	}
}
</style>
